import {SetState} from '@wandb/weave/common/util/types';
import React, {useContext} from 'react';

import {ProjectListViewMode} from '../../components/ExpandableProjectList';
import {WBSlateElementType} from '../../components/Slate/WBSlate/types';

export const ENTITY_PAGE_STATE_CURRENT_VERSION = 2;

export type EntityPageSection =
  | `teamReadme`
  | `projects`
  | `activity`
  | `runs`
  | `reports`
  | `links`;

export type ShowcasedProject = {
  id: string;
  url: string;
  name: string;
  user: string;
  entityName: string; // can be different from user when the user is a team
  lastRun: string;
  runCount: number;
};

export type ShowcasedReport = {
  id: string;
  url: string;
  name: string;
  description: string;
  imageURL: string;
  lastEdit: string;
  viewCount: number;
};

export type ShowcasedLink = {
  url: string;
  name: string;
};

export type EntityPageState = {
  version: typeof ENTITY_PAGE_STATE_CURRENT_VERSION;
  reportSpec: WBSlateElementType[] | null;
  sectionOrder: EntityPageSection[];
  projectListViewMode: ProjectListViewMode;
  showcasedProjectIDs: string[];
  showcasedReportIDs: string[];
  links: ShowcasedLink[];
  teamReadme: WBSlateElementType[] | null;
};

export type EntityPageStateV0 = Omit<
  EntityPageStateV1,
  `version` | `showcasedReportIDs`
> & {
  version: 0;
  showcasedReports: ShowcasedReport[];
};

export type EntityPageStateV1 = Omit<
  EntityPageStateV2,
  `version` | `showcasedProjectIDs`
> & {version: 1};

export type EntityPageStateV2 = EntityPageState & {version: 2};

export const ENTITY_PAGE_INITIAL_STATE: EntityPageState = {
  version: ENTITY_PAGE_STATE_CURRENT_VERSION,
  reportSpec: null,
  teamReadme: null,
  sectionOrder: [
    `teamReadme`,
    `projects`,
    `links`,
    `activity`,
    `runs`,
    `reports`,
  ],
  projectListViewMode: `tile`,
  showcasedProjectIDs: [],
  showcasedReportIDs: [],
  links: [],
};

type EntityPageProps = {
  profileViewID: string;
  entityName: string;
  entityIsTeam: boolean;
  readOnly: boolean;
  isOwnPersonalEntity: boolean;
};

export type EntityPageContextType = EntityPageState & EntityPageProps;

export const EntityPageContext =
  React.createContext<EntityPageContextType | null>(null);

export function useEntityPageContext(): EntityPageContextType {
  const value = useContext(EntityPageContext);
  if (value == null) {
    throw new Error(
      `useEntityPageContext called outside of EntityPageContext.Provider`
    );
  }
  return value;
}

type EntityPageUpdaters = {
  setReportSpec: SetState<WBSlateElementType[] | null>;
  setSectionOrder: SetState<EntityPageSection[]>;
  setProjectListViewMode: SetState<ProjectListViewMode>;
  setShowcasedProjectIDs: SetState<string[]>;
  setShowcasedReportIDs: SetState<string[]>;
  setLinks: SetState<ShowcasedLink[]>;
  setTeamReadme: SetState<WBSlateElementType[] | null>;
};

export const EntityPageUpdaterContext =
  React.createContext<EntityPageUpdaters | null>(null);

export function useEntityPageUpdaterContext(): EntityPageUpdaters {
  const value = useContext(EntityPageUpdaterContext);
  if (value == null) {
    throw new Error(
      `useEntityPageUpdaterContext called outside of EntityPageUpdaterContext.Provider`
    );
  }
  return value;
}
